import { taskExpandIntent } from './taskExpand';
import { taskBoardIntent } from './taskBoardIntent';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';

export { ShareIntentError } from './errors';
export { taskExpandIntent };
export { taskBoardIntent };

/**
 * Registry of all share intents, keyed by intent.type. Future intents
 * (tag-zoom, doc-view, scan-position, etc.) plug in by adding an import +
 * registry entry here — no changes to MainView.vue or the Vuex module needed.
 */
export const SHARE_INTENTS = {
  [taskExpandIntent.type]: taskExpandIntent,
  [taskBoardIntent.type]: taskBoardIntent,
};

/**
 * Walk the registry and return the first matching intent for a given Vue
 * Router query object. Returns null if no intent matches.
 *
 * @param {Record<string, string>} query
 * @returns {object | null}
 */
export function decodeFromQuery(query) {
  if (!query) return null;
  for (const intent of Object.values(SHARE_INTENTS)) {
    const parsed = intent.decode(query);
    if (parsed) return parsed;
  }
  return null;
}

/**
 * Map of intent type → encoder. Centralizes the encoder lookup so
 * `copyTaskLinkToClipboard` can pick the right encoder by intent name
 * without hard-coding the registry's structure.
 */
const ENCODERS = {
  [taskExpandIntent.type]: taskExpandIntent.encode,
  [taskBoardIntent.type]: taskBoardIntent.encode,
};

/**
 * Encode a task into a share-intent URL and write it to the clipboard.
 * Centralizes the boilerplate previously duplicated across Kanban/Gantt/List
 * task views. Each caller used to repeat: read facility/taskType from store,
 * call taskExpandIntent.encode, write to clipboard, show toast — now a single
 * helper handles all of it.
 *
 * The `intent` parameter selects which share-intent encoder to use. Defaults
 * to `'task-expand'` for back-compat with existing call sites that haven't
 * been migrated to the menu pattern yet. Pass `'task-board'` when the
 * destination facility has no tags for this task (the URL will land the
 * recipient on the Kanban view instead of an empty 3D viewer).
 *
 * @param {{ facilityId: string, taskId: string, taskTypeId: string, intent?: 'task-expand' | 'task-board', dialog?: object }} args
 * @returns {Promise<boolean>} true on success, false on failure
 */
export async function copyTaskLinkToClipboard({
  facilityId,
  taskId,
  taskTypeId,
  intent = taskExpandIntent.type,
  dialog,
}) {
  if (!facilityId || !taskTypeId) {
    dialog?.notify?.error?.('Missing facility or task type — cannot copy link');
    return false;
  }
  const encode = ENCODERS[intent];
  if (!encode) {
    debugError(`[copyTaskLinkToClipboard] unknown intent="${intent}"`);
    dialog?.notify?.error?.('Could not copy link — unknown intent');
    return false;
  }
  try {
    const url = encode({ facilityId, taskId, taskTypeId });
    await navigator.clipboard.writeText(url);
    dialog?.notify?.success?.('Link copied to clipboard');
    debugLog(`[copyTaskLinkToClipboard] intent=${intent} url=${url}`);
    return true;
  } catch (err) {
    debugError('[copyTaskLinkToClipboard] failed', err);
    dialog?.notify?.error?.('Could not copy link');
    return false;
  }
}
